import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, CheckCircle2, MapPin, MessageSquareText, Search } from 'lucide-react';
import SignalMap3D from './SignalMap3D';
import { prefersReducedMotion } from './motion';
import './scanConsole.css';

const API = process.env.REACT_APP_API_URL || 'http://localhost:4000';
const TYPING_SAMPLES = ['yourbusiness.com', 'acmeplumbing.com', 'coastaldental.com', 'summitroofing.com'];

const parseFindings = scan => {
  if (!scan) return [];
  const value = typeof scan.findings === 'string' ? JSON.parse(scan.findings) : scan.findings;
  return Array.isArray(value) ? value : [];
};

/* Cycles example domains through the placeholder with a typing effect. */
function useTypingPlaceholder(active) {
  const [text, setText] = useState(TYPING_SAMPLES[0]);
  const state = useRef({ sample: 0, char: TYPING_SAMPLES[0].length, deleting: false });

  useEffect(() => {
    if (!active || prefersReducedMotion()) { setText(TYPING_SAMPLES[0]); return undefined; }
    let timer;
    const tick = () => {
      const current = state.current;
      const word = TYPING_SAMPLES[current.sample];
      if (!current.deleting) {
        current.char += 1;
        setText(word.slice(0, current.char));
        if (current.char >= word.length) { current.deleting = true; timer = setTimeout(tick, 1400); return; }
      } else {
        current.char -= 1;
        setText(word.slice(0, current.char));
        if (current.char <= 0) { current.deleting = false; current.sample = (current.sample + 1) % TYPING_SAMPLES.length; }
      }
      timer = setTimeout(tick, current.deleting ? 45 : 95);
    };
    timer = setTimeout(tick, 1400);
    return () => clearTimeout(timer);
  }, [active]);

  return text;
}

export default function ScanConsole() {
  const [form, setForm] = useState({ website: '', location: '' });
  const [state, setState] = useState({ loading: false, error: '', scan: null });
  const placeholder = useTypingPlaceholder(!form.website && !state.scan && !state.loading);

  const submit = async event => {
    event.preventDefault();
    setState({ loading: true, error: '', scan: null });
    try {
      const hostname = form.website.replace(/^https?:\/\//i, '').split('/')[0].replace(/^www\./, '');
      const token = localStorage.getItem('dt_token');
      const response = await fetch(`${API}/tools/visibility-scan`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
        body: JSON.stringify({ business: hostname || 'Website scan', website: form.website, location: form.location }),
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'The scan could not be completed.');
      setState({ loading: false, error: '', scan: data.scan });
    } catch (error) {
      setState({ loading: false, error: error.message, scan: null });
    }
  };

  const scan = state.scan;
  const findings = parseFindings(scan);
  const passed = findings.filter(item => item.passed).length;
  const issues = findings.length - passed;
  const score = scan ? Number(scan.score) : null;
  const sample = !scan; // dimmed example figures before a real scan

  return (
    <section className="scan-console" id="visibility-scan" aria-label="Free visibility scan">
      <header className="scan-console-head">
        <p className="scan-console-title"><span aria-hidden="true">&gt;</span> Free Visibility Scan<span className="scan-caret" aria-hidden="true">_</span></p>
        <p className="scan-console-sub">Check any local business website against seven local-search signals. No account required.</p>
      </header>

      <form className="scan-bar" onSubmit={submit}>
        <span className="scan-bar-icon" aria-hidden="true"><Search size={17} /></span>
        <label className="scan-bar-field">
          <span className="sr-only">Website URL</span>
          <input value={form.website} onChange={event => setForm(current => ({ ...current, website: event.target.value }))} placeholder={placeholder} required />
        </label>
        <label className="scan-bar-field is-market">
          <span className="sr-only">Target city or market</span>
          <input value={form.location} onChange={event => setForm(current => ({ ...current, location: event.target.value }))} placeholder="City or market" required />
        </label>
        <button className="scan-bar-btn" type="submit" disabled={state.loading}>
          {state.loading ? 'Scanning...' : 'Scan Now'}
        </button>
      </form>

      {state.error && <p className="scan-console-error" role="alert">{state.error}</p>}

      <div className="scan-cards">
        <article className={`scan-card${sample ? ' is-sample' : ''}`}>
          <header><span>VISIBILITY SCORE</span>{sample ? <em>Sample</em> : <CheckCircle2 size={15} />}</header>
          <div className="scan-ring" style={{ '--score': `${(score ?? 72) * 3.6}deg` }}>
            <span>{score === null ? '72%' : `${score}%`}</span>
          </div>
          <small>{scan ? 'Live result for your site' : 'Run a scan to see your real score'}</small>
        </article>

        <article className={`scan-card${sample ? ' is-sample' : ''}`}>
          <header><span>SIGNALS PASSED</span>{sample ? <em>Sample</em> : <MapPin size={15} />}</header>
          <div className="scan-figure">
            <strong>{scan ? passed : 5}</strong>
            <b>/ {scan ? findings.length : 7}</b>
          </div>
          <small>{scan ? 'Local-search signals in place' : 'Seven signals are checked'}</small>
        </article>

        <article className={`scan-card${sample ? ' is-sample' : ''}`}>
          <header><span>ISSUES FOUND</span>{sample ? <em>Sample</em> : <MessageSquareText size={15} />}</header>
          <div className="scan-figure">
            <strong className={(scan ? issues : 2) > 0 ? 'is-alert' : ''}>{scan ? issues : 2}</strong>
          </div>
          <small>{scan ? 'Fixes ranked by impact' : 'Gaps costing you customers'}</small>
        </article>

        <article className="scan-card is-map">
          <SignalMap3D />
          <span className="scan-map-label">Local market signal</span>
        </article>
      </div>

      {scan && (
        <div className="scan-result-bar">
          <ul className="scan-findings">
            {findings.map(item => (
              <li key={item.key} className={item.passed ? 'is-pass' : 'is-fail'}>
                <i aria-hidden="true" />{item.label}
              </li>
            ))}
          </ul>
          <Link to="/signup" className="scan-result-cta">Save this scan &amp; track rankings <ArrowRight size={15} /></Link>
        </div>
      )}
    </section>
  );
}
